/**
 * zoom-dialog.js
 *
 * 修复 horizontal-carousel 布局下点击图片无法打开放大弹窗的完整方案：
 *
 * 问题根因：
 *   1. horizontal-carousel 分支中的 zoom-dialog 未渲染（已通过 PHP 修复）
 *   2. data-zoom-index 在某些情况下渲染为空（已通过 PHP 修复）
 *   3. JS 事件委托中查找 zoom-dialog 的逻辑不够健壮
 *   4. customElements.define 时机问题导致 querySelector 找不到元素
 *
 * 本文件对应对方案：
 *   1. 全局事件委托兼容所有触发器类型
 *   2. 多级兜底查找 zoom-dialog（同 media-gallery → 全局 ID → 重试）
 *   3. DOMContentLoaded 后延迟初始化
 *   4. 点击整个 .hc-slide 区域都能触发（不依赖内部 button）
 *   5. 完善的错误处理和调试日志
 */

/* ========== <zoom-dialog> 自定义元素 ========== */
if (!customElements.get('zoom-dialog')) {
  class ZoomDialog extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('dialog');
      this.media = Array.from(this.querySelectorAll('.product-media-container'));
      this.thumbnails = this.querySelector('.dialog-thumbnails-list');
      this.highResImagesLoaded = new Set();

      this.handleScroll = this.debounce(this.handleScrollEvent.bind(this), 50);

      if (this.dialog) {
        this.dialog.addEventListener('scroll', this.handleScroll);
        // 点击 dialog 遮罩区域关闭
        this.dialog.addEventListener('click', e => {
          if (e.target === this.dialog) {
            this.close();
          }
        });
      }

      // ESC 键关闭
      this._onKeyDown = e => {
        if (e.key === 'Escape' && this.dialog && this.dialog.open) {
          this.close();
        }
      };
      document.addEventListener('keydown', this._onKeyDown);

      // 标记已初始化
      this._initialized = true;
    }

    connectedCallback() {
      // 绑定 zoom-dialog 内部所有带 data-zoom-index 的触发器
      const triggers = this.querySelectorAll('[data-zoom-index]');
      triggers.forEach(trigger => {
        // 避免重复绑定
        if (trigger._zoomBound) return;
        trigger._zoomBound = true;
        trigger.addEventListener('click', e => {
          const rawIndex = trigger.getAttribute('data-zoom-index');
          const index = parseInt(rawIndex, 10);
          if (isNaN(index)) {
            console.warn('[zoom-dialog] Invalid zoom index:', rawIndex, 'on trigger:', trigger);
            return;
          }
          this.open(index, e);
        });
      });

      // 如果 media 列表为空，重新查询（处理动态加载场景）
      if (this.media.length === 0 && this.dialog) {
        this.media = Array.from(this.querySelectorAll('.product-media-container'));
      }
    }

    disconnectedCallback() {
      if (this.dialog) {
        this.dialog.removeEventListener('scroll', this.handleScroll);
      }
      if (this._onKeyDown) {
        document.removeEventListener('keydown', this._onKeyDown);
      }
    }

    async open(index, event) {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
      }

      const numericIndex = parseInt(index, 10);
      if (isNaN(numericIndex)) {
        console.warn('[zoom-dialog] open() called with invalid index:', index);
        return;
      }

      // 确保 media 列表是最新的
      if (this.media.length === 0) {
        this.media = Array.from(this.querySelectorAll('.product-media-container'));
      }

      const targetImage = this.media[numericIndex];
      const targetThumbnail = this.thumbnails ? this.thumbnails.children[numericIndex] : null;

      if (!targetImage) {
        console.warn(
          '[zoom-dialog] Target image not found at index:', numericIndex,
          'media count:', this.media.length,
          'dialog:', this
        );
        return;
      }

      const openDialog = () => {
        if (!this.dialog) return;
        try {
          // 如果已经打开，先关闭再重新打开（避免重复 showModal 报错）
          if (this.dialog.open) {
            this.dialog.close();
          }
          if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
          } else {
            this.dialog.setAttribute('open', '');
          }
        } catch (err) {
          console.warn('[zoom-dialog] showModal failed, using fallback:', err);
          this.dialog.setAttribute('open', '');
        }

        // 滚动到目标缩略图和目标图片
        if (targetThumbnail) {
          targetThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
        targetImage.scrollIntoView({ behavior: 'smooth' });
      };

      // 支持 carousel / slideshow / horizontal-carousel / grid 多种 sourceImage
      let sourceImage = null;
      if (event && event.target instanceof Element) {
        sourceImage = event.target.closest(
          'li, slideshow-slide, .hc-slide, .product-media-container, .media-gallery__grid-item, .product-media, [data-zoom-index]'
        );
      }

      if (!this.supportsViewTransitions() || !sourceImage) {
        openDialog();
        this.selectThumbnail(numericIndex, { behavior: 'auto' });
        return;
      }

      const transitionName = 'gallery-item';
      sourceImage.style.setProperty('view-transition-name', transitionName);

      try {
        await this.startViewTransition(() => {
          openDialog();
          if (sourceImage) sourceImage.style.removeProperty('view-transition-name');
          targetImage.style.setProperty('view-transition-name', transitionName);
        });
      } catch (err) {
        console.warn('[zoom-dialog] View transition failed:', err);
        openDialog();
      }

      targetImage.style.removeProperty('view-transition-name');
      this.selectThumbnail(numericIndex, { behavior: 'auto' });
    }

    closeDialog() {
      if (this.dialog) {
        try {
          if (typeof this.dialog.close === 'function' && this.dialog.open) {
            this.dialog.close();
          } else {
            this.dialog.removeAttribute('open');
          }
        } catch (err) {
          this.dialog.removeAttribute('open');
        }
      }
      window.dispatchEvent(new CustomEvent('dialog-close'));
    }

    async close() {
      if (!this.supportsViewTransitions()) return this.closeDialog();

      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      if (!mostVisibleElement) return this.closeDialog();

      const activeIndex = this.media.indexOf(mostVisibleElement);

      // 兼容 data-presentation 属性与 property 两种取值方式
      const mediaGallery = this.closest('media-gallery');
      const presentation =
        mediaGallery?.getAttribute('data-presentation') ||
        mediaGallery?.presentation ||
        '';
      const isCarouselLike =
        presentation === 'carousel' ||
        presentation === 'horizontal-carousel';

      // 查找对应的外部 slide 节点用于 view transition
      let slide = null;
      if (isCarouselLike && mediaGallery && mediaGallery.slideshow) {
        try {
          slide = mediaGallery.slideshow.slides?.[activeIndex];
        } catch (e) {}
      }
      if (!slide && mediaGallery) {
        // 尝试多种选择器
        const selectors = [
          'slideshow-slide:nth-child(' + (activeIndex + 1) + ')',
          '.hc-slide[data-zoom-index="' + activeIndex + '"]',
          '.hc-slide:nth-child(' + (activeIndex + 1) + ')',
          '.media-gallery__grid-item:nth-child(' + (activeIndex + 1) + ')'
        ];
        for (const sel of selectors) {
          slide = mediaGallery.querySelector(sel);
          if (slide) break;
        }
      }

      if (!slide) {
        console.warn('[zoom-dialog] No matching slide found for close transition, index:', activeIndex);
        return this.closeDialog();
      }

      this.dialog.classList.add('dialog--closed');
      await this.onAnimationEnd(this.thumbnails);

      const transitionName = 'gallery-item';
      mostVisibleElement.style.setProperty('view-transition-name', transitionName);

      try {
        await this.startViewTransition(() => {
          mostVisibleElement.style.removeProperty('view-transition-name');
          slide.style.setProperty('view-transition-name', transitionName);
          this.closeDialog();
        });
      } catch (err) {
        console.warn('[zoom-dialog] Close view transition failed:', err);
        this.closeDialog();
      }

      slide.style.removeProperty('view-transition-name');
      this.dialog.classList.remove('dialog--closed');
    }

    async handleScrollEvent() {
      const mostVisibleElement = await this.getMostVisibleElement(this.media);
      if (!mostVisibleElement) return;

      const activeIndex = this.media.indexOf(mostVisibleElement);
      if (activeIndex < 0) return;

      const targetThumbnail = this.thumbnails ? this.thumbnails.children[activeIndex] : null;
      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      const buttons = this.thumbnails.querySelectorAll('button');
      buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
      });

      this.loadHighResolutionImage(mostVisibleElement);
      this.dispatchEvent(
        new CustomEvent('zoom-media:selected', { detail: { index: activeIndex } })
      );
    }

    async handleThumbnailClick(index) {
      const numericIndex = parseInt(index, 10);
      if (isNaN(numericIndex)) return;
      const behavior = this.prefersReducedMotion() ? 'auto' : 'smooth';
      this.selectThumbnail(numericIndex, { behavior });
    }

    async selectThumbnail(index, options = { behavior: 'smooth' }) {
      const numericIndex = parseInt(index, 10);
      if (
        isNaN(numericIndex) ||
        numericIndex < 0 ||
        !this.thumbnails ||
        !this.thumbnails.children.length
      ) {
        return;
      }

      const safeIndex = Math.min(numericIndex, this.thumbnails.children.length - 1);
      const targetThumbnail = this.thumbnails.children[safeIndex];
      if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

      const buttons = this.thumbnails.querySelectorAll('button');
      buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', i === safeIndex ? 'true' : 'false');
      });

      this.scrollIntoView(targetThumbnail, {
        ancestor: this.thumbnails,
        behavior: options.behavior || 'smooth',
        block: 'center',
        inline: 'center',
      });

      const targetImage = this.media[safeIndex];
      if (targetImage) {
        targetImage.scrollIntoView({ behavior: options.behavior || 'smooth' });
        this.loadHighResolutionImage(targetImage);
      }

      this.dispatchEvent(
        new CustomEvent('zoom-media-selected', { detail: { index: safeIndex } })
      );
    }

    loadHighResolutionImage(mediaContainer) {
      if (!mediaContainer || !mediaContainer.classList.contains('product-media-container--image')) {
        return false;
      }

      const image = mediaContainer.querySelector('img.product-media__image');
      if (!image || !(image instanceof HTMLImageElement)) return false;

      const highResolutionUrl = image.getAttribute('data_max_resolution');
      if (!highResolutionUrl || this.highResImagesLoaded.has(highResolutionUrl)) return false;

      this.preloadImage(highResolutionUrl);

      const newImage = new Image();
      newImage.className = image.className;
      newImage.alt = image.alt;
      newImage.setAttribute('data_max_resolution', highResolutionUrl);

      newImage.onload = () => {
        if (image.parentNode) {
          image.replaceWith(newImage);
        }
        this.highResImagesLoaded.add(highResolutionUrl);
      };

      newImage.src = highResolutionUrl;
    }

    getMostVisibleElement(elements) {
      return new Promise(resolve => {
        if (!elements || !elements.length) return resolve(null);

        let observer;
        // 超时兜底，防止 observer 永远不触发
        const timeout = setTimeout(() => {
          if (observer) observer.disconnect();
          // 返回第一个元素作为兜底
          resolve(elements[0] || null);
        }, 800);

        observer = new IntersectionObserver(
          entries => {
            if (!entries.length) return;
            const mostVisible = entries.reduce((prev, current) =>
              current.intersectionRatio > prev.intersectionRatio ? current : prev
            );
            if (observer) observer.disconnect();
            clearTimeout(timeout);
            resolve(mostVisible.target);
          },
          { threshold: [0, 0.25, 0.5, 0.75, 1] }
        );

        for (const element of elements) {
          if (element) observer.observe(element);
        }
      });
    }

    debounce(func, wait) {
      let timeout;
      return function debounced(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    supportsViewTransitions() {
      return 'startViewTransition' in document;
    }

    async startViewTransition(callback) {
      if (this.supportsViewTransitions()) {
        return document.startViewTransition(callback);
      }
      callback();
      return null;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    preloadImage(src) {
      if (!src) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    }

    onAnimationEnd(element) {
      return new Promise(resolve => {
        if (!element) return resolve();
        const handler = () => {
          element.removeEventListener('animationend', handler);
          resolve();
        };
        element.addEventListener('animationend', handler);
      });
    }

    scrollIntoView(
      element,
      { ancestor, behavior = 'smooth', block = 'start', inline = 'start' } = {}
    ) {
      if (!element) return;

      if (!ancestor) {
        return element.scrollIntoView({ behavior, block, inline });
      }

      const ancestorRect = ancestor.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const calculateScrollOffset = (
        alignment,
        ancestorStart,
        ancestorLength,
        elemStart,
        elemLength,
        currentScroll
      ) => {
        switch (alignment) {
          case 'start':
            return currentScroll + elemStart - ancestorStart;
          case 'center':
            return currentScroll + elemStart - ancestorStart - ancestorLength / 2 + elemLength / 2;
          case 'end':
            return currentScroll + elemStart - ancestorStart - ancestorLength + elemLength;
          default:
            return currentScroll;
        }
      };

      const scrollTop =
        ancestor.scrollHeight > ancestor.clientHeight
          ? calculateScrollOffset(
              block,
              ancestorRect.top,
              ancestor.clientHeight,
              elementRect.top,
              elementRect.height,
              ancestor.scrollTop
            )
          : ancestor.scrollTop;

      const scrollLeft =
        ancestor.scrollWidth > ancestor.clientWidth
          ? Math.max(
              0,
              calculateScrollOffset(
                inline,
                ancestorRect.left,
                ancestor.clientWidth,
                elementRect.left,
                elementRect.width,
                ancestor.scrollLeft
              )
            )
          : ancestor.scrollLeft;

      ancestor.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: behavior,
      });
    }
  }

  customElements.define('zoom-dialog', ZoomDialog);
}

/* ========== 全局事件委托（核心修复） ========== */
(function() {
  'use strict';

  /**
   * 查找 zoom-dialog 元素（多级兜底）
   */
  function findZoomDialog(trigger, targetSelector) {
    // 方式1：从同一 media-gallery 内查找
    const mediaGallery = trigger.closest('media-gallery');
    if (mediaGallery) {
      const zd = mediaGallery.querySelector('zoom-dialog');
      if (zd) return zd;
    }

    // 方式2：按 ID 全局查找
    if (targetSelector) {
      const zd = document.querySelector(targetSelector);
      if (zd) return zd;
    }

    // 方式3：查找页面上第一个 zoom-dialog
    const allDialogs = document.querySelectorAll('zoom-dialog');
    if (allDialogs.length > 0) {
      console.warn('[zoom-dialog] Using first available zoom-dialog as fallback');
      return allDialogs[0];
    }

    return null;
  }

  /**
   * 处理点击事件
   */
  function handleClick(e) {
    const trigger = e.target.closest('[data-zoom-target]');
    if (!trigger) return;

    const targetSelector = trigger.getAttribute('data-zoom-target');
    if (!targetSelector) return;

    // 获取 zoom index
    const rawIndex = trigger.getAttribute('data-zoom-index');
    const index = parseInt(rawIndex, 10);

    if (isNaN(index)) {
      console.warn(
        '[zoom-dialog] data-zoom-index is empty or invalid!',
        'raw value:', rawIndex,
        'trigger:', trigger,
        'selector:', targetSelector
      );
      return;
    }

    // 查找 zoom-dialog
    let zoomDialog = findZoomDialog(trigger, targetSelector);

    // 如果没找到，延迟重试（等待 DOM 更新 / customElements 注册完成）
    if (!zoomDialog) {
      console.warn('[zoom-dialog] Dialog not found immediately, retrying in 100ms...');
      setTimeout(() => {
        zoomDialog = findZoomDialog(trigger, targetSelector);
        if (zoomDialog) {
          console.log('[zoom-dialog] Found dialog on retry');
          doOpen(zoomDialog, index, e, trigger);
        } else {
          console.error(
            '[zoom-dialog] Still not found after retry. Selector:', targetSelector,
            'Available zoom-dialogs:', document.querySelectorAll('zoom-dialog').length
          );
        }
      }, 100);
      return;
    }

    doOpen(zoomDialog, index, e, trigger);
  }

  /**
   * 执行打开放大弹窗
   */
  function doOpen(zoomDialog, index, e, trigger) {
    // 判断是否是可缩放的触发器
    const isZoomable =
      trigger.classList.contains('product-media-container--zoomable') ||
      trigger.classList.contains('hc-slide') ||
      trigger.classList.contains('product-media-container__zoom-button') ||
      trigger.hasAttribute('data-zoom-index') ||
      !!trigger.querySelector('.product-media-container--zoomable') ||
      !!trigger.closest('.product-media-container--zoomable');

    if (isZoomable) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

      // 确保 zoom-dialog 已初始化
      if (typeof zoomDialog.open === 'function') {
        zoomDialog.open(index, e);
      } else {
        console.warn('[zoom-dialog] zoomDialog.open is not a function', zoomDialog);
      }
      return;
    }

    // 缩略图点击
    if (trigger.classList.contains('dialog-thumbnails-list__thumbnail')) {
      if (typeof zoomDialog.handleThumbnailClick === 'function') {
        zoomDialog.handleThumbnailClick(index);
      }

      const swiperThumbnail = document.querySelector(
        '.swiper-controls__thumbnail[data-index="' + index + '"]'
      );
      if (swiperThumbnail) {
        swiperThumbnail.click();
      }
    }
  }

  // 使用捕获阶段监听，确保在其他事件处理器之前执行
  document.addEventListener('click', handleClick, true);

  // 调试辅助：输出当前 zoom-dialog 状态
  window.__zoomDialogDebug = function() {
    const dialogs = document.querySelectorAll('zoom-dialog');
    console.log('=== Zoom Dialog Debug ===');
    console.log('Total zoom-dialog elements:', dialogs.length);
    dialogs.forEach((d, i) => {
      console.log('[' + i + ']', 'id:', d.id, 'initialized:', !!d._initialized, 'media count:', d.querySelectorAll('.product-media-container').length);
    });
    const hcSlides = document.querySelectorAll('.hc-slide[data-zoom-index]');
    console.log('HC slides with data-zoom-index:', hcSlides.length);
    hcSlides.forEach((s, i) => {
      console.log('  slide[' + i + '] index:', s.getAttribute('data-zoom-index'), 'target:', s.getAttribute('data-zoom-target'));
    });
    return 'Check console for details';
  };

})();

/* ========== <scroll-hint> 自定义元素 ========== */
if (!customElements.get('scroll-hint')) {
  class ScrollHint extends HTMLElement {
    constructor() {
      super();
      this.resizeObserver = new ResizeObserver(() => this.update());
    }

    connectedCallback() {
      const throttled =
        window.theme && window.theme.utils && typeof window.theme.utils.rafThrottle === 'function'
          ? window.theme.utils.rafThrottle(this.update.bind(this))
          : this.update.bind(this);

      this._throttledUpdate = throttled;
      this.addEventListener('scroll', this._throttledUpdate);
      if (this.resizeObserver) this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      if (this._throttledUpdate) {
        this.removeEventListener('scroll', this._throttledUpdate);
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    update = () => {
      const {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollLeft,
        scrollWidth,
        clientWidth,
      } = this;

      if (scrollHeight <= clientHeight && scrollWidth <= clientWidth) {
        this.style.maskImage = '';
        return;
      }

      const scrollDirection = scrollWidth > clientWidth ? 'horizontal' : 'vertical';
      const denominator =
        scrollDirection === 'vertical'
          ? scrollHeight - clientHeight
          : scrollWidth - clientWidth;

      const scrollPercentage = denominator > 0
        ? (scrollDirection === 'vertical' ? scrollTop : scrollLeft) / denominator
        : 0;

      if (Number.isNaN(scrollPercentage)) {
        this.style.maskImage = '';
        return;
      }

      this.style.maskImage =
        'linear-gradient(' +
        'to ' + (scrollDirection === 'vertical' ? 'bottom' : 'right') + ',' +
        'transparent ' + (scrollPercentage > 0 ? 1 : 0) + '%,' +
        'black ' + (scrollPercentage < 0.1 ? scrollPercentage * 100 : 10) + '%,' +
        'black ' + (scrollPercentage > 0.9 ? scrollPercentage * 100 : 90) + '%,' +
        'transparent 100%' +
        ')';
    };
  }

  customElements.define('scroll-hint', ScrollHint);
}
